import { ISession } from "@workspace/apiservice/src/interface/session";
import { IRouteLaunchArguments } from "../interface";
import { session } from "@workspace/apiservice/src";
import { injectLog } from '../../../interface/util';
import { IServiceWorkerStatus } from "../../../interface/serviceworker";

export enum ILaunchState {
    Initializing = 0,
    UpdatingSite = 1,
    AwaitingUserDownloadSkipConfirmation = 2,
    DownloadingMedia = 3,
    Completed = 4,
    Halted = 5
}

export enum ILaunchEvent {
    None = 0,
    Start = 1,
    FetchingSiteData = 2,
    FetchingUserSettings = 3,
    CheckForServiceWorker = 4,
    AwaitingUserSelectionMediaDownload = 5,
    UserRequestedDownloadingMedia = 6,
    UserRequestedSkipMedia = 7,
    DownloadingMedia = 8,
    Completed = 9,
    Halt = 10,
}

export interface IStateProcessorLaunchState {
    session?: ISession;
    swState?: IServiceWorkerStatus;
    updateSiteProgress?: number;
    downloadMediaProgress?: number;
    route: IRouteLaunchArguments;
}

function _start(processor: IStateProcessorPrv): Promise<void> {
    return new Promise(async function(resolve, reject) {
        try {
            const workspace = await session(processor.state().route.sessionName);

            processor.setState({
                ...processor.state(),
                session: workspace,
            });

            resolve();
        } catch (error) {
            reject(error);
        }
    });
}

export function _updateSite(processor: IStateProcessorPrv): Promise<void> {
    const log = injectLog();

    const canUpdate = navigator.onLine;

    if ( !canUpdate ) { return Promise.resolve(); }

    const hasContentInDb = async () => {
        try {
            const sites = await processor.state().session?.db?.site().all();
            return sites?.length ?? 0 > 0;    
        } catch (error) {
            return false;
        }
    }
 
    return new Promise(async function(resolve, reject) {
        try {
            const promise = processor.state().session?.operation?.updateSite();

            if ( !promise ) {
                throw Error("No session update promise");
            }

            promise?.onProgress(progress => {
                processor.setState({
                    ...processor.state(),
                    updateSiteProgress: progress,
                })
            });

            promise
                .then(_ => resolve())
                .catch(err => reject(err));
        } catch (error) {
            if ( await hasContentInDb() ) {
                log.warn(`Unable to update: ${error}, allowing skip`);
                resolve();
            } else {
                reject(error);
            }
        }        
    })
}

function ProcessEvent(processor: IStateProcessorPrv, event: ILaunchEvent) {
    const log = injectLog();

    if ( processor.status() === ILaunchState.Halted ) { return; }

    log.info('Processing launch-state event ' + event);

    switch (event) {
        case ILaunchEvent.Start:
            _start(processor)
                .then(_ => {
                    processor.setStatus(ILaunchState.UpdatingSite);
                    ProcessEvent(processor, ILaunchEvent.FetchingSiteData);
                })
                .catch(err => {
                    processor.setError(`Failed to fetch site data: ${err}`);
                    processor.setStatus(ILaunchState.Halted);
                });
            break;

        case ILaunchEvent.FetchingSiteData:
            _updateSite(processor)
                .then(async _ => { 
                    ProcessEvent(processor, ILaunchEvent.Completed);
                })
                .catch(err => {
                    processor.setError(`Failed to fetch site data: ${err}`);
                    processor.setStatus(ILaunchState.Halted);
                });
            break;

        case ILaunchEvent.Completed:
            processor.setStatus(ILaunchState.Completed);
            break;

        case ILaunchEvent.Halt:
            processor.setStatus(ILaunchState.Halted);
            break;

        default:
        case ILaunchEvent.None:
            throw Error(`Unsupported event: ${event}`);
    }
}

type IStateProcessorCallback<T> = (model: T) => void;

export interface IStateProcessor<S, E, U> {
    status: () => S;
    setStatus: (status: S) => void;
    setError: (error: string) => void;
    error: () => string;
    onStatusChange: (callback: IStateProcessorCallback<S>) => void;
    process: (event: E) => void;
    isRunning: () => void;
    state: () => U;
    setState: (state: U) => void;
    onStateChange: (callback: IStateProcessorCallback<U>) => void;
}

interface IStateProcessorPrv extends IStateProcessor<ILaunchState, ILaunchEvent, IStateProcessorLaunchState> {
    _status: ILaunchState,
    _error: string,
    _callbackStatus?: (state: ILaunchState) => void;
    _state: IStateProcessorLaunchState;
    _callbackState?: (model: IStateProcessorLaunchState) => void;
}

function StateProcessor(route: IRouteLaunchArguments): IStateProcessor<ILaunchState, ILaunchEvent, IStateProcessorLaunchState> {
    const manager: IStateProcessorPrv = {
        _status: ILaunchState.Initializing,
        _error: '',
        status: function(this: IStateProcessorPrv) { return this._status; },
        setStatus: function(this: IStateProcessorPrv, status: ILaunchState) { 
            this._status = status;
            this._callbackStatus?.(status);
        },
        error: function(this: IStateProcessorPrv) { return this._error; },
        setError: function(this: IStateProcessorPrv, error: string) { 
            this._error = error;
        },
        onStatusChange: function(this: IStateProcessorPrv, callback: IStateProcessorCallback<ILaunchState>) {
            this._callbackStatus = callback;
        },
        process: function(this: IStateProcessorPrv, event: ILaunchEvent) {
            ProcessEvent(this, event);
        },
        isRunning: function(this: IStateProcessorPrv) {
            return this._status !== ILaunchState.Completed;
        },
        _state: {route,},
        state: function(this: IStateProcessorPrv) {
            return this._state;
        },
        setState: function(this: IStateProcessorPrv, state: IStateProcessorLaunchState) {
            this._state = state;
            this._callbackState?.(state);
        },
        onStateChange: function(this: IStateProcessorPrv, callback: IStateProcessorCallback<IStateProcessorLaunchState>) {
            this._callbackState = callback;
        },
    };

    return manager as IStateProcessor<ILaunchState, ILaunchEvent, IStateProcessorLaunchState>;
}

export default StateProcessor
